/**
 * Atlas de Hunt — tipo → faixa → espécie (≤250) ou zona (≥300).
 * Consome speciesHuntEntries / migrateLegacyHuntState de balance-core.mjs.
 */
import { t, formatNumber, onLocaleChange } from '../i18n/index.js';
import * as balanceCore from '../balance-core.mjs';
import { uiIcon } from '../ui-icons.js';

const esc = (v) => String(v ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));

/** HANDOFF-STUB */
export function migrateLegacyHuntState(state) {
  if (typeof balanceCore.migrateLegacyHuntState === 'function') {
    return balanceCore.migrateLegacyHuntState(state);
  }
  if (!state || typeof state !== 'object') return state;
  if (!state.huntMode) state.huntMode = 'species';
  if (state.huntSpecies == null) {
    state.huntSpecies = null;
    state.huntLevel = balanceCore.routeLevelAt?.(state.range) ?? ((Number(state.range) || 0) * 10 || 1);
  }
  return state;
}

/** Catálogo completo ou filtrado por tipo/faixa. */
export function speciesHuntEntries(filter = null, fallbackRows = []) {
  const all = typeof balanceCore.speciesHuntEntries === 'function'
    ? (balanceCore.speciesHuntEntries() || [])
    : [];
  if (!filter) return all.length ? all : [];
  const { type, band } = filter;
  const rows = all.filter((entry) => (
    (type == null || entry.type === type)
    && (band == null || entry.band === band)
  ));
  if (all.length) return rows;
  const level = balanceCore.routeLevelAt?.(band) ?? 1;
  return (fallbackRows || []).map((row) => ({
    slug: row.slug,
    level,
    band,
    type,
    weight: row.chance || row.weight || 1,
    boss: level >= 200,
    zone: level >= (balanceCore.ZONE_HUNT_MIN_LEVEL || 300),
  }));
}

let root = null;
let hooks = {
  types: () => [],
  ranges: () => [],
  typeLabel: (x) => x,
  typeColor: () => '#777',
  typeGlyph: () => '●',
  getEntries: () => [],
  isSeen: () => false,
  isCaught: () => false,
  pokemonImage: () => '',
  getSpeciesTypes: () => [],
  title: (s) => s,
  trainerLevel: () => 1,
  isTypeUnlocked: () => true,
  isBandUnlocked: () => true,
  bandAccess: () => ({ unlocked: true, hint: '' }),
  bandLabel: (band) => String(bandLevel(band)),
  zoneConfig: () => null,
  zoneProgress: () => ({ seenSpecials: 0 }),
  zoneAccess: () => ({ unlocked: true, hint: '' }),
  now: () => Date.now(),
  onStartHunt: () => {},
  showToast: () => {},
  getDifficulty: () => 0,
  setDifficulty: () => {},
  initialType: 0,
  initialBand: 0,
};
const ui = { open: false, typeIndex: 0, band: 0, query: '', selected: '', lockedBand: null, searchFocus: false };
const spriteCenterCache = new Map();

function applySpriteCenter(img) {
  const apply = () => {
    const src = img.currentSrc || img.src;
    const renderedWidth = img.getBoundingClientRect().width || 58;
    const renderedHeight = img.getBoundingClientRect().height || 58;
    const cacheKey = `${src}@${Math.round(renderedWidth)}x${Math.round(renderedHeight)}`;
    if (spriteCenterCache.has(cacheKey)) {
      const cached = spriteCenterCache.get(cacheKey);
      img.style.setProperty('--sprite-shift-x', `${cached.x}px`);
      img.style.setProperty('--sprite-shift-y', `${cached.y}px`);
      return;
    }
    if (!img.naturalWidth || !img.naturalHeight) return;
    const canvas = document.createElement('canvas');
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
    const context = canvas.getContext('2d', { willReadFrequently: true });
    if (!context) return;
    context.drawImage(img, 0, 0);
    let pixels;
    try { pixels = context.getImageData(0, 0, canvas.width, canvas.height).data; }
    catch { return; }
    let minX = canvas.width, minY = canvas.height, maxX = -1, maxY = -1;
    for (let y = 0; y < canvas.height; y += 1) {
      for (let x = 0; x < canvas.width; x += 1) {
        if (pixels[(y * canvas.width + x) * 4 + 3] < 16) continue;
        minX = Math.min(minX, x); minY = Math.min(minY, y);
        maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
      }
    }
    if (maxX < minX || maxY < minY) return;
    const shift = {
      x: Math.round(((canvas.width - 1) / 2 - (minX + maxX) / 2) * renderedWidth / canvas.width),
      y: Math.round(((canvas.height - 1) / 2 - (minY + maxY) / 2) * renderedHeight / canvas.height),
    };
    spriteCenterCache.set(cacheKey, shift);
    img.style.setProperty('--sprite-shift-x', `${shift.x}px`);
    img.style.setProperty('--sprite-shift-y', `${shift.y}px`);
  };
  if (img.complete) apply();
  else img.addEventListener('load', apply, { once: true });
}

function currentType() {
  return hooks.types()[ui.typeIndex] || null;
}

function bandLevel(band = ui.band) {
  return hooks.ranges()[band]?.[0] ?? 1;
}

function isZoneBand(band = ui.band) {
  if (typeof balanceCore.isZoneHuntBand === 'function') return balanceCore.isZoneHuntBand(band);
  return bandLevel(band) >= (balanceCore.ZONE_HUNT_MIN_LEVEL || 300);
}

function renderTypes() {
  return `<div class="atlas-type-list">
    ${hooks.types().map((tp, i) => {
      const name = Array.isArray(tp) ? tp[0] : tp;
      const color = Array.isArray(tp) ? tp[2] : hooks.typeColor(name);
      const speciesCount = new Set(hooks.getEntries(name, null).map((entry) => entry.slug)).size;
      const unlocked = speciesCount > 0;
      return `<button type="button" class="atlas-type ${i === ui.typeIndex ? 'selected' : ''}" data-atlas-type="${i}" style="--type-color:${esc(color)}" ${unlocked ? '' : 'disabled'}>
        <span class="atlas-type-icon">${hooks.typeGlyph(name)}</span>
        <span class="atlas-type-copy"><b>${esc(hooks.typeLabel(name))}</b><small>${esc(t('atlas.speciesCount', { count: speciesCount }))}</small></span>
      </button>`;
    }).join('')}
  </div>`;
}

const BAND_ICONS = [
  'sparkles', 'sun-high', 'diamond', 'swords', 'shield', 'star', 'award', 'crown',
  'skull', 'flame', 'sun-high', 'flame', 'skull', 'crown', 'award',
];

function visibleBands() {
  const type = currentType();
  const typeName = Array.isArray(type) ? type[0] : type;
  return hooks.ranges().map((range, i) => ({
    range,
    i,
    speciesCount: isZoneBand(i)
      ? hooks.getEntries(null, i).filter((entry) => !entry.legendaryBoss).length
      : hooks.getEntries(typeName, i).length,
    unlocked: hooks.isBandUnlocked(i),
  })).filter(({ speciesCount }) => speciesCount > 0);
}

function ensureVisibleBand() {
  const bands = visibleBands();
  if (!bands.some(({ i }) => i === ui.band)) ui.band = bands[0]?.i ?? 0;
  return bands;
}

function renderBands() {
  const bands = ensureVisibleBand();
  const selectedLocked = bands.find(({ i, unlocked }) => i === ui.lockedBand && !unlocked);
  const selectedAccess = selectedLocked ? hooks.bandAccess(selectedLocked.i) : null;
  return `<div class="atlas-bands-wrap">
    <small class="eyebrow">${esc(t('atlas.pickLevel'))}</small>
    <nav class="atlas-bands">
    ${bands.map(({ range, i, speciesCount, unlocked }) => {
      const level = range[0];
      const zone = isZoneBand(i);
      const access = hooks.bandAccess(i) || { unlocked, hint: '' };
      const hint = unlocked ? '' : access.hint;
      return `<button type="button" class="atlas-band ${i === ui.band ? 'selected' : ''} ${level >= 200 ? 'boss-band' : ''} ${zone ? 'zone-band' : ''} ${unlocked ? '' : 'locked'}" data-atlas-band="${i}" ${hint ? `title="${esc(hint)}"` : ''}>
        <span class="atlas-band-icon">${uiIcon(BAND_ICONS[i] || 'star')}</span>
        <span class="atlas-band-copy"><b>${esc(zone ? hooks.bandLabel(i) : `${t('common.lv')} ${level}`)}</b><em>${esc(t('atlas.speciesCount', { count: speciesCount }))}</em></span>
        ${zone ? `<strong>${esc(t('atlas.zoneTag'))}</strong>` : level >= 200 ? `<strong>${esc(t('atlas.bossTag'))}</strong>` : ''}
      </button>`;
    }).join('')}
    </nav>
  </div>
  ${selectedAccess?.hint ? `<aside class="atlas-unlock-panel" role="status"><span>🔒</span><div><small>${esc(t('atlas.unlockRequirement'))}</small><b>${esc(selectedAccess.hint)}</b><p>${esc(t('atlas.unlockHint'))}</p></div></aside>` : ''}`;
}

function renderSpecies() {
  const type = currentType();
  const typeName = Array.isArray(type) ? type[0] : type;
  const rows = hooks.getEntries(typeName, ui.band);
  const q = ui.query.trim().toLowerCase();
  const filtered = rows.filter((row) => {
    if (!q) return true;
    return row.slug.includes(q) || hooks.title(row.slug).toLowerCase().includes(q);
  });
  if (ui.selected && !filtered.some((r) => r.slug === ui.selected)) ui.selected = filtered[0]?.slug || '';

  return `<div class="atlas-species-pane">
    <label class="atlas-search"><span>⌕</span><input id="atlasSearch" value="${esc(ui.query)}" placeholder="${esc(t('atlas.search'))}"></label>
    <div class="atlas-species-grid">
      ${filtered.length ? filtered.map((row) => {
        const caught = hooks.isCaught(row.slug);
        const selected = row.slug === ui.selected;
        const name = hooks.title(row.slug);
        const speciesTypes = hooks.getSpeciesTypes(row.slug);
        return `<button type="button" class="atlas-species ${selected ? 'selected' : ''} ${caught ? 'caught' : 'not-caught'} ${row.boss ? 'boss' : ''}" data-atlas-species="${esc(row.slug)}">
          <span class="atlas-species-art" style="overflow:hidden"><img src="${esc(hooks.pokemonImage(row.slug))}" alt="${esc(name)}" style="display:block;object-fit:contain;object-position:center;transform:translate(var(--sprite-shift-x,0),var(--sprite-shift-y,0))"></span>
          <span class="atlas-species-copy"><b>${esc(name)}</b><small>${esc(t('common.lv'))} ${row.level}</small></span>
          <span class="atlas-species-types">${speciesTypes.map((type) => `<i style="--type-color:${esc(hooks.typeColor(type))}" title="${esc(hooks.typeLabel(type))}">${hooks.typeGlyph(type)}</i>`).join('')}</span>
          ${row.boss ? `<strong class="atlas-boss-badge">${esc(t('atlas.bossTag'))}</strong>` : ''}
          <em class="atlas-caught-state">${caught ? `✓ ${esc(t('atlas.caught'))}` : `○ ${esc(t('atlas.notCaught'))}`}</em>
        </button>`;
      }).join('') : `<div class="atlas-empty">${esc(t('atlas.empty'))}</div>`}
    </div>
  </div>`;
}

function formatRotateCountdown(ms) {
  const total = Math.max(0, Math.floor(Number(ms) / 1000));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, '0')}m`;
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function renderZone() {
  const zone = hooks.zoneConfig(ui.band);
  const rows = hooks.getEntries(null, ui.band).filter((row) => !row.legendaryBoss);
  const access = hooks.zoneAccess(ui.band) || { unlocked: true, hint: '' };
  const now = hooks.now?.() || Date.now();
  const featured = zone?.specialKind ? balanceCore.featuredSpecialSpecies(zone, now) : null;
  const rotateMs = balanceCore.msUntilNextGmt3Hour(now);
  const odds = zone?.specialChancePerKill ? Math.round(1 / zone.specialChancePerKill) : null;
  const kindLabel = zone?.specialKind === 'mythical' ? t('zone.mythicalBoss') : t('zone.legendaryBoss');
  const specialGuide = !zone?.specialKind
    ? `<aside class="atlas-zone-legendary-guide">
        <span>${uiIcon('sparkles')}</span>
        <div><b>${esc(t('zone.noSpecialTitle'))}</b><p>${esc(t('zone.noSpecialHint'))}</p></div>
      </aside>`
    : `<aside class="atlas-zone-legendary-guide">
        <span>${uiIcon('sparkles')}</span>
        <div>
          <b>${esc(t('zone.hourlyFocusTitle'))}</b>
          <p>${esc(t('zone.hourlyFocusGuide', { kind: kindLabel, name: hooks.title(featured || ''), n: odds }))}</p>
          <p>${esc(t('zone.rotationCountdown', { time: formatRotateCountdown(rotateMs) }))} · GMT-3</p>
          <p>${esc(t('zone.captureHint'))}</p>
        </div>
      </aside>
      <div class="atlas-zone-featured">
        <small>${esc(t('zone.hourlyFeatured'))}</small>
        <figure>
          <img src="${esc(hooks.pokemonImage(featured || ''))}" alt="${esc(hooks.title(featured || ''))}">
          <figcaption>${esc(hooks.title(featured || ''))}</figcaption>
        </figure>
      </div>
      <div class="atlas-zone-bosses"><small>${esc(t('zone.specialPool'))}</small>${(zone.specialPool || []).map((slug) => `<span class="${slug === featured ? 'is-featured' : ''}" title="${esc(hooks.title(slug))}"><img src="${esc(hooks.pokemonImage(slug))}" alt="${esc(hooks.title(slug))}"></span>`).join('')}</div>`;
  return `<div class="atlas-zone-pane">
    <div class="atlas-zone-card">
      <span class="eyebrow">${esc(t('atlas.zoneTag'))}</span>
      <h3>${esc(t(`zones.${zone?.id}.title`))}</h3>
      <p>${esc(t(`zones.${zone?.id}.description`))}</p>
      <div class="atlas-zone-stats">
        <span><small>${esc(t('zone.recommendedPower'))}</small><b>${esc(formatNumber(zone?.recommendedPower || 0))}</b></span>
        <span><small>${esc(t('zone.hourlyFeatured'))}</small><b>${featured ? esc(hooks.title(featured)) : esc(t('zone.noSpecial'))}</b></span>
        <span><small>${esc(t('zone.rewardBonus'))}</small><b>+${Math.round(((zone?.effects?.xpMult || 1) - 1) * 100)}% XP</b></span>
      </div>
      ${specialGuide}
      <p class="atlas-zone-effects">${esc(t('zone.effectsSummary', { xp: Math.round(((zone?.effects?.xpMult || 1) - 1) * 100), loot: Math.round(((zone?.effects?.lootMult || 1) - 1) * 100), damage: Math.round(((zone?.effects?.wildDamageMult || 1) - 1) * 100) }))}</p>
      ${access.unlocked ? '' : `<p class="atlas-zone-lock">🔒 ${esc(access.hint)}</p>`}
      <div class="atlas-zone-pool">
        ${rows.length ? rows.map((row) => {
          const name = hooks.title(row.slug);
          return `<figure class="atlas-zone-species">
            <span class="atlas-zone-art"><img src="${esc(hooks.pokemonImage(row.slug))}" alt="${esc(name)}"></span>
            <figcaption>${esc(name)}</figcaption>
          </figure>`;
        }).join('') : `<div class="atlas-empty">${esc(t('atlas.empty'))}</div>`}
      </div>
      <p class="atlas-zone-hint">${esc(t('atlas.zoneAoeHint'))}</p>
    </div>
  </div>`;
}

function canStart() {
  if (!hooks.isBandUnlocked(ui.band)) return false;
  if (isZoneBand()) return hooks.zoneAccess(ui.band).unlocked;
  return !!ui.selected;
}

/** Nível real do encontro: nível da faixa de hunt + dificuldade (+0/+3/+5). */
function previewEncounterLevel() {
  if (isZoneBand()) return bandLevel();
  const difficulty = Number(hooks.getDifficulty()) || 0;
  const base = bandLevel();
  if (typeof balanceCore.encounterLevel === 'function') {
    const minLevel = ui.selected
      ? (balanceCore.speciesMinRouteLevel?.(ui.selected) || 1)
      : base;
    return balanceCore.encounterLevel(
      { minLevel, maxLevel: 300 },
      base,
      difficulty,
    );
  }
  return Math.max(1, base + difficulty);
}

function startLabel(level) {
  if (isZoneBand()) {
    const zone = hooks.zoneConfig(ui.band);
    return t('atlas.startZone', { type: t(`zones.${zone?.id}.title`), level: hooks.bandLabel(ui.band) });
  }
  return t('atlas.startHunt', { name: ui.selected ? hooks.title(ui.selected) : '—', level });
}

function renderDifficulty() {
  const cur = Number(hooks.getDifficulty()) || 0;
  return `<div class="atlas-difficulty" role="group" aria-label="${esc(t('atlas.difficulty'))}">
    <small>${esc(t('atlas.difficulty'))}</small>
    ${[0, 3, 5].map((d) => `<button type="button" class="${cur === d ? 'active' : ''}" data-atlas-diff="${d}" title="${d === 0 ? 'Nível da faixa' : `+${d} níveis acima da faixa: mais XP e loot, mais perigo`}">+${d}</button>`).join('')}
  </div>`;
}

function render() {
  if (!root) return;
  ensureVisibleBand();
  const type = currentType();
  const typeName = Array.isArray(type) ? type[0] : type;
  const selected = ui.selected;
  const level = previewEncounterLevel();
  const zone = isZoneBand();

  root.innerHTML = `<div class="atlas-overlay-card" role="dialog" aria-modal="true" aria-labelledby="atlasTitle">
    <button type="button" class="dialog-close" data-atlas-action="close" aria-label="${esc(t('atlas.close'))}">&times;</button>
    <header class="atlas-head">
      <span class="eyebrow">${esc(t('atlas.eyebrow'))}</span>
      <h2 id="atlasTitle">${esc(t('atlas.title'))}</h2>
      <p>${esc(t(zone ? 'atlas.zoneIntroShort' : 'atlas.intro'))}</p>
    </header>
    <div class="atlas-layout">
      <aside class="atlas-types">
        <small class="eyebrow">${esc(t('atlas.pickType'))}</small>
        ${renderTypes()}
      </aside>
      <div class="atlas-main">
        ${renderBands()}
        ${zone ? renderZone() : renderSpecies()}
      </div>
    </div>
    <footer class="atlas-foot">
      <div class="atlas-foot-meta">
        <span>${esc(zone ? t(`zones.${hooks.zoneConfig(ui.band)?.id}.title`) : hooks.typeLabel(typeName || ''))}</span>
        <span>${esc(zone ? hooks.bandLabel(ui.band) : `${t('common.lv')} ${level}`)}</span>
        ${!zone && selected ? `<b>${esc(hooks.title(selected))}</b>` : ''}
        ${zone ? `<b>${esc(t('atlas.zoneTag'))}</b>` : ''}
      </div>
      ${!zone ? renderDifficulty() : ''}
      <button type="button" class="atlas-start" data-atlas-action="start" ${canStart() ? '' : 'disabled'}>
        ${esc(startLabel(level))}
      </button>
    </footer>
  </div>`;

  root.querySelectorAll('.atlas-species-art img').forEach((img) => {
    img.style.imageRendering = 'pixelated';
    applySpriteCenter(img);
  });
  root.querySelectorAll('.atlas-zone-art img, .atlas-zone-bosses img').forEach((img) => {
    img.style.imageRendering = 'pixelated';
    applySpriteCenter(img);
  });
  if (ui.searchFocus) {
    const input = root.querySelector('#atlasSearch');
    if (input) {
      input.focus();
      const len = input.value.length;
      input.setSelectionRange(len, len);
    }
  }
}

function bind() {
  root.addEventListener('click', (event) => {
    if (event.target === root) { closeAtlas(); return; }
    if (event.target.closest('[data-atlas-action="close"]')) { closeAtlas(); return; }
    const typeBtn = event.target.closest('[data-atlas-type]');
    if (typeBtn) {
      ui.typeIndex = Number(typeBtn.dataset.atlasType);
      ui.selected = '';
      ui.query = '';
      ui.lockedBand = null;
      render();
      return;
    }
    const bandBtn = event.target.closest('[data-atlas-band]');
    if (bandBtn) {
      ui.band = Number(bandBtn.dataset.atlasBand);
      ui.selected = '';
      ui.query = '';
      ui.lockedBand = hooks.isBandUnlocked(ui.band) ? null : ui.band;
      render();
      return;
    }
    const diffBtn = event.target.closest('[data-atlas-diff]');
    if (diffBtn) { hooks.setDifficulty(Number(diffBtn.dataset.atlasDiff)); render(); return; }
    const species = event.target.closest('[data-atlas-species]');
    if (species) { ui.selected = species.dataset.atlasSpecies; render(); return; }
    if (event.target.closest('[data-atlas-action="start"]')) {
      if (!canStart()) return;
      const level = previewEncounterLevel();
      try {
        if (isZoneBand()) {
          hooks.onStartHunt({
            typeIndex: ui.typeIndex,
            band: ui.band,
            zone: true,
            level,
          });
        } else {
          if (!ui.selected) return;
          hooks.onStartHunt({
            typeIndex: ui.typeIndex,
            band: ui.band,
            species: ui.selected,
            level,
          });
        }
        closeAtlas();
      } catch (error) {
        console.error('[Atlas] start hunt failed', error);
        hooks.showToast?.(t('atlas.startFailed'));
      }
    }
  });
  root.addEventListener('input', (event) => {
    if (event.target.id === 'atlasSearch') {
      ui.query = event.target.value;
      ui.searchFocus = true;
      render();
    }
  });
  root.addEventListener('focusin', (event) => {
    if (event.target.id === 'atlasSearch') ui.searchFocus = true;
  });
  root.addEventListener('focusout', (event) => {
    if (event.target.id === 'atlasSearch') ui.searchFocus = false;
  });
}

export function initAtlasUI(options = {}) {
  hooks = { ...hooks, ...options };
  root = document.createElement('div');
  root.id = 'atlasOverlay';
  root.className = 'atlas-overlay hidden';
  document.body.append(root);
  bind();
  onLocaleChange(() => { if (ui.open) render(); });
  addEventListener('keydown', (event) => {
    if (event.key === 'Escape' && ui.open) closeAtlas();
  });
}

export function openAtlas(opts = {}) {
  if (!root) return;
  ui.open = true;
  ui.typeIndex = opts.typeIndex ?? hooks.initialType ?? 0;
  ui.band = opts.band ?? hooks.initialBand ?? 0;
  ui.selected = opts.species || '';
  ui.query = '';
  ui.lockedBand = null;
  ui.searchFocus = false;
  root.classList.remove('hidden');
  document.body.classList.add('atlas-open');
  render();
}

export function closeAtlas() {
  if (!ui.open) return;
  ui.open = false;
  root.classList.add('hidden');
  document.body.classList.remove('atlas-open');
}

export function isAtlasOpen() {
  return ui.open;
}
